import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import axios from "axios"; import { handleAuthError } from "../../utils";
import * as Yup from "yup";
import { useFormik } from "formik";
import DeleteModal from "../../components/Common/DeleteModal";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import withAuth from "../withAuth";
import { baseUrl } from '../../Network';
import { usePagination } from "../../hooks/usePagination";
import PaginationControls from "../../components/Common/PaginationControls";
import { useConfirm } from "../../contexts/ConfirmContext";

const Settings = () => {
  document.title = "Dashboard | RYD Admin";
  const { showConfirm } = useConfirm();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("authUser") || "null");
    } catch {
      return null;
    }
  }, []);
  const isCurrentSuperAdmin = !!currentUser?.isSuperAdmin;

  useEffect(() => {
    fetchAdmins();
  }, []);

  useEffect(() => {
    document.title = isEdit
      ? "Edit Admin | RYD Admin"
      : "Add New Admin | RYD Admin";
  }, [isEdit]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/admin/all`);
      setAdmins(response.data.data);
    } catch (error) { handleAuthError(error)
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    setIsEdit(false);
    setSelectedAdmin(null);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: selectedAdmin ? selectedAdmin.fullName : "",
      email: selectedAdmin ? selectedAdmin.email : "",
      password: "",
      displayName: selectedAdmin ? selectedAdmin.displayName : "",
      role: selectedAdmin ? selectedAdmin.role.toString() : "0",
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Please Enter Full Name"),
      email: Yup.string().email().required("Please Enter Email"),
      password: Yup.string().required("Please Enter Password"),
      displayName: Yup.string().required("Please Enter Display Name"),
      role: Yup.string().required("Please Select Role"),
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        if (isEdit) {
          await editAdmin(values);
        } else {
          await createAdmin(values);
        }
        resetForm();
      } catch (error) { handleAuthError(error)
        console.error("Error:", error);
      }
    },
  });

  const editAdmin = async (values) => {
    try {
      if (!isCurrentSuperAdmin) {
        toast.error("Only super admins can edit admins");
        return;
      }
      const response = await axios.put(`${baseUrl}/admin/${selectedAdmin.id}`, values);
      const responseData = response.data?.data || response.data;

      const updatedAdmins = admins.map((admin) =>
        admin.id === selectedAdmin.id ? { ...admin, ...responseData } : admin
      );
      setAdmins(updatedAdmins);
      toast.success("Admin updated successfully");

      toggleModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      toast.error(error.response?.data?.message || "Failed to update admin");
    }
  };

  const createAdmin = async (values) => {
    try {
      if (!isCurrentSuperAdmin) {
        toast.error("Only super admins can create admins");
        return;
      }
      await axios.post(`${baseUrl}/admin/auth/create`, values);

      const updatedAdminList = await axios.get(`${baseUrl}/admin/all`);
      setAdmins(updatedAdminList.data.data);
      toast.success("Admin created successfully");

      toggleModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      toast.error(error.response?.data?.message || "Failed to create admin");
    }
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setIsEdit(true);
    toggleModal();
  };

  const handleDeleteAdmin = async (admin) => {
    try {
      if (!isCurrentSuperAdmin) {
        toast.error("Only super admins can delete admins");
        return;
      }
      if (Number(currentUser?.id) === Number(admin.id)) {
        toast.error("You cannot delete your own account");
        return;
      }

      const confirmed = await showConfirm({
        title: "Delete admin",
        message: "Are you sure you want to delete this admin?",
        variant: "danger",
      });
      if (!confirmed) return;

      await axios.delete(`${baseUrl}/admin/${admin.id}`);
      const updatedAdmins = admins.filter((a) => a.id !== admin.id);
      setAdmins(updatedAdmins);
      toast.success("Admin deleted successfully");
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      toast.error(error.response?.data?.message || "Failed to delete admin");
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAdmins = admins.filter((admin) =>
    admin.fullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const {
    paginatedData: paginatedAdmins,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    showAll,
    handlePageChange,
    handlePageSizeChange,
    goToNextPage,
    goToPreviousPage,
    canPreviousPage,
    canNextPage,
    paginatedDataLength,
  } = usePagination(filteredAdmins, 20);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Dashboard" breadcrumbItem="Settings" />
          <Row>
            <Col>
              <div className="mb-3">
                <h5 className="card-title">
                  Admin List
                  <span className="text-muted fw-normal ms-2">
                    ({admins.length})
                  </span>
                </h5>
              </div>
              <Row className="align-items-center">
                <Col lg="12">
                  <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                    <div>
                      {isCurrentSuperAdmin ? (
                        <Link
                          to="#"
                          className="btn btn-light"
                          onClick={toggleModal}
                        >
                          <i className="bx bx-plus me-1"></i> Add New Admin
                        </Link>
                      ) : (
                        <p className="text-muted mb-0 small">
                          Only a super admin can create or edit admins.
                        </p>
                      )}
                    </div>
                    <div>
                      <Input
                        type="text"
                        placeholder="Search by Name"
                        onChange={handleSearchChange}
                        value={searchTerm}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
              {loading ? (
                <div className="text-center mt-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
              ) : filteredAdmins.length === 0 ? (
                <div className="text-center mt-5">
                  <h3>No data available</h3>
                </div>
              ) : (
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Display Name</th>
                      <th>Role</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAdmins.map((admin, index) => {
                      const globalIndex = showAll 
                        ? index 
                        : (currentPage - 1) * pageSize + index;
                      return (
                        <tr key={admin.id}>
                          <td>{globalIndex + 1}</td>
                        <td>{admin.fullName}</td>
                        <td>{admin.email}</td>
                        <td>{admin.displayName}</td>
                        <td>
                          {admin.role === 1 ? "Super Admin" : "Normal Admin"}
                        </td>
                        <td>
                          {isCurrentSuperAdmin ? (
                            <>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleEditAdmin(admin)}
                              >
                                Edit
                              </Button>{" "}
                              {Number(currentUser?.id) !== Number(admin.id) ? (
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDeleteAdmin(admin)}
                                >
                                  Delete
                                </Button>
                              ) : null}
                            </>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {filteredAdmins.length > 0 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  showAll={showAll}
                  handlePageChange={handlePageChange}
                  handlePageSizeChange={handlePageSizeChange}
                  goToNextPage={goToNextPage}
                  goToPreviousPage={goToPreviousPage}
                  canPreviousPage={canPreviousPage}
                  canNextPage={canNextPage}
                  paginatedDataLength={paginatedDataLength}
                />
              )}
            </Col>
          </Row>
        </Container>

        <Modal isOpen={modal} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>
            {isEdit ? "Edit Admin" : "Add New Admin"}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={validation.handleSubmit}>
              <Label for="fullName">Full Name</Label>
              <Input
                type="text"
                name="fullName"
                id="fullName"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.fullName}
                invalid={
                  validation.touched.fullName && !!validation.errors.fullName
                }
              />
              <FormFeedback>{validation.errors.fullName}</FormFeedback>
              <Label for="email">Email</Label>
              <Input
                type="email"
                name="email"
                id="email"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.email}
                invalid={validation.touched.email && !!validation.errors.email}
              />
              <FormFeedback>{validation.errors.email}</FormFeedback>
              <Label for="password">Password</Label>
              <Input
                type="password"
                name="password"
                id="password"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.password || ""}
                invalid={
                  validation.touched.password && !!validation.errors.password
                }
              />
              <FormFeedback>{validation.errors.password}</FormFeedback>
              <Label for="displayName">Display Name</Label>
              <Input
                type="text"
                name="displayName"
                id="displayName"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.displayName}
                invalid={
                  validation.touched.displayName &&
                  !!validation.errors.displayName
                }
              />
              <FormFeedback>{validation.errors.displayName}</FormFeedback>
              <Label for="role">Role</Label>
              <Input
                type="select"
                name="role"
                id="role"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.role}
                invalid={validation.touched.role && !!validation.errors.role}
              >
                <option value="">Select Role</option>
                <option value="0">Normal Admin</option>
                <option value="1">Super Admin</option>
              </Input>
              <FormFeedback>{validation.errors.role}</FormFeedback>
              <Button className="mt-3" color="primary" type="submit">
                {isEdit ? "Update Admin" : "Add Admin"}
              </Button>{" "}
              <Button className="mt-3" color="secondary" onClick={toggleModal}>
                Cancel
              </Button>
            </Form>
          </ModalBody>
        </Modal>

        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteAdmin(selectedAdmin)}
          onCloseClick={() => setDeleteModal(false)}
        />

        <ToastContainer />
      </div>
    </React.Fragment>
  );
};

export default withAuth(Settings);
